<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import CampaignsAPI from 'dashboard/api/campaigns';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportHeader from './components/ReportHeader.vue';

const { t, locale } = useI18n();

const isLoading = ref(false);
const errorMessage = ref('');
const analytics = ref({ summary: {}, campaigns: [], timeline: [] });
const filters = reactive({ from: '', to: '' });

const summary = computed(() => ({
  recipients: 0,
  pending: 0,
  sent: 0,
  delivered: 0,
  read: 0,
  failed: 0,
  responded: 0,
  ignored: 0,
  delivery_rate: 0,
  read_rate: 0,
  response_rate: 0,
  ...analytics.value.summary,
}));

const metricCards = computed(() => [
  {
    key: 'recipients',
    label: t('CAMPAIGN_REPORTS.METRICS.RECIPIENTS'),
    icon: 'i-lucide-users',
    color: 'text-n-blue-11',
  },
  {
    key: 'sent',
    label: t('CAMPAIGN_REPORTS.METRICS.SENT'),
    icon: 'i-lucide-send',
    color: 'text-n-blue-11',
  },
  {
    key: 'delivered',
    label: t('CAMPAIGN_REPORTS.METRICS.DELIVERED'),
    icon: 'i-lucide-circle-check',
    color: 'text-n-teal-11',
  },
  {
    key: 'read',
    label: t('CAMPAIGN_REPORTS.METRICS.READ'),
    icon: 'i-lucide-eye',
    color: 'text-n-teal-11',
  },
  {
    key: 'responded',
    label: t('CAMPAIGN_REPORTS.METRICS.RESPONDED'),
    icon: 'i-lucide-message-circle-reply',
    color: 'text-n-teal-11',
  },
  {
    key: 'ignored',
    label: t('CAMPAIGN_REPORTS.METRICS.IGNORED'),
    icon: 'i-lucide-message-circle-off',
    color: 'text-n-amber-11',
  },
  {
    key: 'failed',
    label: t('CAMPAIGN_REPORTS.METRICS.FAILED'),
    icon: 'i-lucide-circle-x',
    color: 'text-n-ruby-11',
  },
  {
    key: 'pending',
    label: t('CAMPAIGN_REPORTS.METRICS.PENDING'),
    icon: 'i-lucide-clock-3',
    color: 'text-n-slate-11',
  },
]);

const rateCards = computed(() => [
  {
    key: 'delivery_rate',
    label: t('CAMPAIGN_REPORTS.METRICS.DELIVERY_RATE'),
    color: 'bg-n-blue-9',
  },
  {
    key: 'read_rate',
    label: t('CAMPAIGN_REPORTS.METRICS.READ_RATE'),
    color: 'bg-n-teal-9',
  },
  {
    key: 'response_rate',
    label: t('CAMPAIGN_REPORTS.METRICS.RESPONSE_RATE'),
    color: 'bg-n-brand',
  },
]);

const fetchAnalytics = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const response = await CampaignsAPI.getAnalytics({
      from: filters.from || undefined,
      to: filters.to || undefined,
    });
    analytics.value = response.data;
  } catch (error) {
    errorMessage.value = t('CAMPAIGN_REPORTS.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const clearFilters = () => {
  filters.from = '';
  filters.to = '';
  fetchAnalytics();
};

const formatDate = value => {
  if (!value) return t('CAMPAIGN_REPORTS.NOT_SCHEDULED');
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const channelLabels = computed(() => ({
  Whatsapp: t('CAMPAIGN_REPORTS.CHANNELS.WHATSAPP'),
  Email: t('CAMPAIGN_REPORTS.CHANNELS.EMAIL'),
  Sms: t('CAMPAIGN_REPORTS.CHANNELS.SMS'),
  TwilioSms: t('CAMPAIGN_REPORTS.CHANNELS.TWILIO_SMS'),
  Website: t('CAMPAIGN_REPORTS.CHANNELS.WEBSITE'),
}));

const channelLabel = value => channelLabels.value[value] || value;
const formatPercentage = value => `${value}%`;

onMounted(fetchAnalytics);
</script>

<template>
  <ReportHeader
    :header-title="t('CAMPAIGN_REPORTS.TITLE')"
    :header-description="t('CAMPAIGN_REPORTS.DESCRIPTION')"
  />

  <section class="flex flex-col min-w-0 gap-4 pb-8">
    <div
      class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 p-4 sm:p-5 rounded-xl border border-n-container bg-n-solid-2"
    >
      <div class="flex flex-col sm:flex-row gap-3 min-w-0">
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN_REPORTS.FILTERS.FROM') }}
          <input
            v-model="filters.from"
            type="date"
            class="h-10 min-w-44 px-3 rounded-lg border border-n-strong bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN_REPORTS.FILTERS.TO') }}
          <input
            v-model="filters.to"
            type="date"
            class="h-10 min-w-44 px-3 rounded-lg border border-n-strong bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          slate
          outline
          :label="t('CAMPAIGN_REPORTS.FILTERS.CLEAR')"
          @click="clearFilters"
        />
        <Button
          :label="t('CAMPAIGN_REPORTS.FILTERS.APPLY')"
          icon="i-lucide-filter"
          :is-loading="isLoading"
          @click="fetchAnalytics"
        />
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="p-4 rounded-xl border border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="isLoading && !analytics.campaigns.length"
      class="flex justify-center py-20"
    >
      <Spinner />
    </div>

    <template v-else>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <article
          v-for="metric in metricCards"
          :key="metric.key"
          class="min-w-0 p-4 rounded-xl border border-n-container bg-n-solid-2"
        >
          <div
            class="flex items-center gap-2 text-sm font-medium text-n-slate-11"
          >
            <span
              :class="[metric.icon, metric.color]"
              class="size-4 shrink-0"
            />
            <span class="break-words">
              {{ metric.label }}
            </span>
          </div>
          <strong class="block mt-2 text-2xl sm:text-3xl text-n-slate-12">
            {{ summary[metric.key] }}
          </strong>
        </article>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <article
          v-for="rate in rateCards"
          :key="rate.key"
          class="p-4 rounded-xl border border-n-container bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-11">
              {{ rate.label }}
            </span>
            <strong class="text-xl text-n-slate-12">
              {{ formatPercentage(summary[rate.key]) }}
            </strong>
          </div>
          <div class="h-2 mt-3 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full rounded-full"
              :class="rate.color"
              :style="{ width: `${Math.min(summary[rate.key], 100)}%` }"
            />
          </div>
        </article>
      </div>

      <article
        class="min-w-0 rounded-xl border border-n-container bg-n-solid-2 overflow-hidden"
      >
        <header class="px-4 sm:px-5 py-4 border-b border-n-container">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('CAMPAIGN_REPORTS.TABLE.TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CAMPAIGN_REPORTS.TABLE.DESCRIPTION') }}
          </p>
        </header>

        <div
          v-if="!analytics.campaigns.length"
          class="px-5 py-14 text-center text-n-slate-11"
        >
          {{ t('CAMPAIGN_REPORTS.EMPTY') }}
        </div>

        <div v-else class="max-w-full overflow-x-auto">
          <table class="w-full min-w-[1080px] border-collapse text-sm">
            <thead class="bg-n-alpha-1 text-n-slate-11">
              <tr>
                <th class="px-4 py-3 text-left font-medium">
                  {{ t('CAMPAIGN_REPORTS.TABLE.CAMPAIGN') }}
                </th>
                <th class="px-4 py-3 text-left font-medium">
                  {{ t('CAMPAIGN_REPORTS.TABLE.CHANNEL') }}
                </th>
                <th class="px-4 py-3 text-left font-medium">
                  {{ t('CAMPAIGN_REPORTS.TABLE.DATE') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.RECIPIENTS') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.DELIVERED') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.READ') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.RESPONDED') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.IGNORED') }}
                </th>
                <th class="px-3 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.FAILED') }}
                </th>
                <th class="px-4 py-3 text-right font-medium">
                  {{ t('CAMPAIGN_REPORTS.METRICS.RESPONSE_RATE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="campaign in analytics.campaigns"
                :key="campaign.id"
                class="border-t border-n-container text-n-slate-12 hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3">
                  <strong class="block max-w-64 break-words">{{
                    campaign.title
                  }}</strong>
                  <span class="text-xs text-n-slate-11">{{
                    campaign.inbox
                  }}</span>
                </td>
                <td class="px-4 py-3">{{ channelLabel(campaign.channel) }}</td>
                <td class="px-4 py-3 whitespace-nowrap">
                  {{ formatDate(campaign.scheduled_at) }}
                </td>
                <td class="px-3 py-3 text-right">
                  {{ campaign.metrics.recipients }}
                </td>
                <td class="px-3 py-3 text-right">
                  {{ campaign.metrics.delivered }}
                </td>
                <td class="px-3 py-3 text-right">
                  {{ campaign.metrics.read }}
                </td>
                <td class="px-3 py-3 text-right text-n-teal-11">
                  {{ campaign.metrics.responded }}
                </td>
                <td class="px-3 py-3 text-right text-n-amber-11">
                  {{ campaign.metrics.ignored }}
                </td>
                <td class="px-3 py-3 text-right text-n-ruby-11">
                  {{ campaign.metrics.failed }}
                </td>
                <td class="px-4 py-3 text-right font-semibold">
                  {{ formatPercentage(campaign.metrics.response_rate) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>

      <p class="text-xs leading-5 text-n-slate-11">
        {{ t('CAMPAIGN_REPORTS.FOOTNOTE') }}
      </p>
    </template>
  </section>
</template>
